import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from '../../../../util/reactIntl';
import { EditComponent } from '..';
import { DirectionsMap, IconClose } from '../../../../components';
import { CheckmarkIcon } from '../../../../icons';
import { MOBILITY_DRIVING, MOBILITY_TWO_WHEELER } from '../../../../marketplace-custom-config';
import { gmLatLng } from '../../../../util/googleMaps';
import ScoreBadge from '../../../../components/ScoreBadge/ScoreBadge';
import css from './SectionLocation.css';
import { useCurrentEntityLocation } from '../../../../hooks/useCurrentEntityLocation';
import useIntersectionObserverForMap from './useIntersectionObserverForMap';
import classNames from 'classnames';
import useStaticMapPreview from '../../../../components/Map/useStaticMapPreview';

const SectionLocation = ({
    currentListing,
    publicData,
    isOwnListing,
    editParams,
    currentUser,
    scoreData,
    isDraftVariant,
}) => {
    const [error, setError] = useState(null);
    const [distance, setDistance] = useState(null);
    const [duration, setDuration] = useState(null);
    const [directions, setDirections] = useState(null);
    const {
        useStaticMap,
        setUseStaticMap,
        activate: handleActivateDynamicMap,
        onKeyDown,
    } = useStaticMapPreview(true /** use static map by default */);

    const rootRef = useRef(null);
    const isOnScreen = useIntersectionObserverForMap(rootRef, { threshold: 0.1 });

    const editAllowed = isOwnListing && !isDraftVariant;

    const currentUserLocationData = useCurrentEntityLocation({
        location: currentUser?.attributes?.profile?.publicData?.userLocation,
    });

    const { geolocation: listingOrigin } = currentListing.attributes;
    const { addressDetails, mobility: mobilityPB } = publicData || {};
    const { postalCode, location } = addressDetails || {};

    const mobility = !!(mobilityPB && mobilityPB.length > 0);

    const currentUserDefined = currentUser && currentUser.id;
    const userMobility = currentUserDefined
        ? currentUser.attributes.profile.publicData.mobility
        : {};
    const preferredTransport =
        userMobility && userMobility.transport ? userMobility.transport : MOBILITY_DRIVING;

    /** Approx. distance between listing & user */
    useEffect(() => {
        const listingLatLng = listingOrigin ? gmLatLng(listingOrigin.lat, listingOrigin.lng) : null;
        const currentUserLocationLng = Boolean(currentUserLocationData?.origin)
            ? gmLatLng(currentUserLocationData.origin.lat, currentUserLocationData.origin.lng)
            : null;

        const { maps } = window.google;
        const { computeDistanceBetween } = maps.geometry.spherical || {};

        if (!currentUserLocationLng || !listingLatLng) {
            return;
        }

        const distance = (
            computeDistanceBetween(currentUserLocationLng, listingLatLng) / 1000
        ).toFixed(0);

        setDistance(distance);
    }, []);

    const resolveDurationByTravelModeSuccess = (result, status) => {
        const isOk = status === window.google.maps.DirectionsStatus.OK;
        if (isOk) {
            setDirections(result);
            setDuration(result.routes[0].legs[0].duration.text);
        } else {
            setError('Error fetching directions', result);
        }
    };

    const resolveDurationByTravelMode = () => {
        const { lat, lng } = currentUserLocationData?.origin || {};

        if (!lat || !lng) return null;

        setUseStaticMap(false);

        const from = gmLatLng(Number(lat), Number(lng));
        const directionsService = new window.google.maps.DirectionsService();

        directionsService.route(
            {
                origin: from,
                destination: gmLatLng(listingOrigin.lat, listingOrigin.lng),
                travelMode:
                    preferredTransport === MOBILITY_TWO_WHEELER
                        ? MOBILITY_DRIVING
                        : preferredTransport,
            },
            resolveDurationByTravelModeSuccess
        );
    };

    if (!listingOrigin || !listingOrigin.lat || !listingOrigin.lng) {
        return null;
    }

    return (
        <div className={css.root} ref={rootRef}>
            <h2
                className={classNames(css.title, {
                    [css.titleWithScore]: Boolean(scoreData && scoreData.baseScoreBreakdown),
                })}
            >
                <span>Standort & Mobilität</span>

                {editAllowed && (
                    <EditComponent pageName="stable-location" editParams={editParams} />
                )}
                {scoreData && scoreData.baseScoreBreakdown && (
                    <div className={css.matchingContainer}>
                        <ScoreBadge
                            scoreData={scoreData.baseScoreBreakdown.distanceAndMobilityScore}
                        />
                    </div>
                )}
            </h2>

            {error && (
                <p className={css.error}>
                    <FormattedMessage id="SectionLocation.calculateTimeForTravelError" />
                </p>
            )}

            <div>
                <div className={css.mobility}>
                    {mobility ? (
                        <CheckmarkIcon rootClassName={css.selectedIcon} />
                    ) : (
                        <IconClose className={css.notSelectedIcon} />
                    )}
                    <p className={!mobility ? css.disabled : undefined}>
                        <FormattedMessage id="SectionLocation.labelMobility" />
                    </p>
                </div>

                <div className={css.flexWrapper}>
                    {postalCode && location && (
                        <div className={css.flexItem}>
                            <div className={css.subTitle}>
                                <FormattedMessage id="SectionLocation.locationSubTitle" />
                            </div>
                            <p>
                                {postalCode} {location}
                            </p>
                        </div>
                    )}
                    {distance && (
                        <div className={css.flexItem}>
                            <div className={css.subTitle}>
                                <FormattedMessage id="SectionLocation.distanceSubTitle" />
                            </div>
                            <p>{distance} km</p>
                        </div>
                    )}

                    {preferredTransport && (
                        <div className={css.flexItem}>
                            <div className={css.subTitle}>
                                <FormattedMessage id="SectionLocation.calculateTimeForTravelOption" />{' '}
                                <FormattedMessage
                                    id={`EditUserMobilitySection.mobility-${preferredTransport}`}
                                />
                            </div>
                            {duration ? (
                                <p>{duration}</p>
                            ) : (
                                <p
                                    className={css.calculateTimeAction}
                                    onClick={resolveDurationByTravelMode}
                                >
                                    <FormattedMessage id="SectionLocation.calculateTimeForTravel" />
                                </p>
                            )}
                        </div>
                    )}
                </div>
                {isOnScreen && (
                    <DirectionsMap
                        geolocation={listingOrigin}
                        directions={directions}
                        mapElement={<div className={css.mapDiv} />}
                        useStaticMap={useStaticMap}
                        handleActivateDynamicMap={handleActivateDynamicMap}
                        onKeyDown={onKeyDown}
                        renderContainerElement={(children, props = {}) => {
                            const { className, ...restProps } = props;
                            return (
                                <div
                                    className={classNames(css.containerDiv, className)}
                                    {...restProps}
                                >
                                    {children}
                                </div>
                            );
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default SectionLocation;
